import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const BulkUploadPanel = ({ onUploadComplete, className = '' }) => {
  const [uploadState, setUploadState] = useState('idle'); // idle, uploading, mapping, processing, complete, error
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [columnMapping, setColumnMapping] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const [jobId, setJobId] = useState(null);
  const fileInputRef = useRef(null);

  const requiredColumns = [
    { key: 'make', label: 'Make', required: true },
    { key: 'model', label: 'Model', required: true },
    { key: 'year', label: 'Year', required: true },
    { key: 'serialNumber', label: 'Serial Number', required: true },
    { key: 'hours', label: 'Hours', required: false },
    { key: 'capacity', label: 'Capacity (tons)', required: false },
    { key: 'boomLength', label: 'Boom Length (ft)', required: false },
    { key: 'condition', label: 'Condition (1-5)', required: false }
  ];

  const mockFileColumns = [
    'Crane Make', 'Crane Model', 'Manufacturing Year', 'Serial #', 
    'Operating Hours', 'Lift Capacity', 'Boom Length', 'Condition Rating',
    'Purchase Price', 'Current Location', 'Notes'
  ];

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setUploadState('mapping');
    // Simulate file parsing
    setTimeout(() => {
      // Mock validation errors
      setValidationErrors([
        { row: 3, column: 'year', error: 'Invalid year format. Expected 4-digit year.' },
        { row: 7, column: 'serialNumber', error: 'Serial number already exists in inventory.' },
        { row: 12, column: 'hours', error: 'Hours must be a positive number.' }
      ]);
    }, 1000);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    const files = Array.from(e?.dataTransfer?.files);
    const file = files?.find(f => 
      f?.type === 'text/csv' || 
      f?.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    );
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
  };

  const handleFileInputChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleColumnMappingChange = (requiredColumn, selectedColumn) => {
    setColumnMapping(prev => ({
      ...prev,
      [requiredColumn]: selectedColumn
    }));
  };

  const handleStartProcessing = () => {
    setUploadState('processing');
    setUploadProgress(0);
    const newJobId = `job_${Date.now()}`;
    setJobId(newJobId);

    // Simulate processing progress
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setUploadState('complete');
          onUploadComplete({
            jobId: newJobId,
            processed: 45,
            successful: 42,
            errors: 3,
            file: selectedFile?.name
          });
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 500);
  };

  const handleReset = () => {
    setUploadState('idle');
    setSelectedFile(null);
    setUploadProgress(0);
    setColumnMapping({});
    setValidationErrors([]);
    setJobId(null);
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    // Mock CSV template download
    const csvContent = `Make,Model,Year,Serial Number,Hours,Capacity,Boom Length,Condition\nCaterpillar,336E,2018,CAT123456,2500,36,60,4\nKomatsu,PC350,2019,KOM789012,1800,35,58,5`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'crane_inventory_template.csv';
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  const downloadErrorReport = () => {
    // Mock error report download
    const errorContent = validationErrors?.map(error => 
      `Row ${error?.row},${error?.column},${error?.error}`
    )?.join('\n');
    const csvContent = `Row,Column,Error\n${errorContent}`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'validation_errors.csv';
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  const getColumnOptions = () => {
    return [
      { value: '', label: 'Select column...' },
      ...mockFileColumns?.map(col => ({ value: col, label: col }))
    ];
  };

  return (
    <div className={`bg-surface border border-border rounded-lg shadow-card ${className}`}>
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text-primary">Bulk Upload</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={downloadTemplate}
            iconName="Download"
            iconPosition="left"
          >
            Template
          </Button>
        </div>
      </div>
      <div className="p-6">
        {uploadState === 'idle' && (
          <div>
            <div
              className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef?.current?.click()}
            >
              <Icon name="Upload" size={48} className="text-text-secondary mx-auto mb-4" />
              <h4 className="text-lg font-medium text-text-primary mb-2">
                Drop files here or click to browse
              </h4>
              <p className="text-text-secondary mb-4">
                Supports CSV and XLSX files up to 10MB
              </p>
              <Button variant="outline">
                Choose File
              </Button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx"
              onChange={handleFileInputChange}
              className="hidden"
            />
          </div>
        )}

        {uploadState === 'mapping' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-text-primary">Column Mapping</h4>
                <p className="text-sm text-text-secondary">
                  Map your file columns to required fields
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                iconName="X"
              />
            </div>

            <div className="bg-muted p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="FileText" size={16} className="text-text-secondary" />
                <span className="text-sm font-medium text-text-primary">
                  {selectedFile?.name}
                </span>
                <span className="text-sm text-text-secondary">
                  ({(selectedFile?.size / 1024)?.toFixed(1)} KB)
                </span>
              </div>
            </div>

            <div className="space-y-4">
              {requiredColumns?.map(column => (
                <div key={column?.key} className="flex items-center space-x-4">
                  <div className="w-1/3">
                    <label className="text-sm font-medium text-text-primary">
                      {column?.label}
                      {column?.required && <span className="text-error ml-1">*</span>}
                    </label>
                  </div>
                  <div className="flex-1">
                    <Select
                      options={getColumnOptions()}
                      value={columnMapping?.[column?.key] || ''}
                      onChange={(value) => handleColumnMappingChange(column?.key, value)}
                      placeholder="Select column..."
                    />
                  </div>
                </div>
              ))}
            </div>

            {validationErrors?.length > 0 && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertTriangle" size={16} className="text-error" />
                    <span className="font-medium text-error">
                      {validationErrors?.length} Validation Errors
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadErrorReport}
                    iconName="Download"
                    iconPosition="left"
                  >
                    Download Report
                  </Button>
                </div>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {validationErrors?.slice(0, 3)?.map((error, index) => (
                    <div key={index} className="text-sm text-error">
                      Row {error?.row}: {error?.error}
                    </div>
                  ))}
                  {validationErrors?.length > 3 && (
                    <div className="text-sm text-text-secondary">
                      +{validationErrors?.length - 3} more errors...
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={handleReset}>
                Cancel
              </Button>
              <Button 
                onClick={handleStartProcessing}
                disabled={!Object.keys(columnMapping)?.length}
                iconName="Play"
                iconPosition="left"
              >
                Start Processing
              </Button>
            </div>
          </div>
        )}

        {uploadState === 'processing' && (
          <div className="space-y-6">
            <div className="text-center">
              <Icon name="Loader2" size={48} className="text-primary mx-auto mb-4 animate-spin" />
              <h4 className="font-medium text-text-primary mb-2">Processing Upload</h4>
              <p className="text-text-secondary">
                Processing {selectedFile?.name}...
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary">Progress</span>
                <span className="text-text-primary">{Math.round(uploadProgress)}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>

            {jobId && (
              <div className="bg-muted p-4 rounded-lg">
                <div className="text-sm">
                  <span className="text-text-secondary">Job ID: </span>
                  <span className="font-mono text-text-primary">{jobId}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {uploadState === 'complete' && (
          <div className="space-y-6">
            <div className="text-center">
              <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-4" />
              <h4 className="font-medium text-text-primary mb-2">Upload Complete</h4>
              <p className="text-text-secondary">
                Successfully processed {selectedFile?.name}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-muted p-4 rounded-lg">
                <div className="text-2xl font-bold text-text-primary">42</div>
                <div className="text-sm text-text-secondary">Successful</div>
              </div>
              <div className="bg-muted p-4 rounded-lg">
                <div className="text-2xl font-bold text-error">3</div>
                <div className="text-sm text-text-secondary">Errors</div>
              </div>
              <div className="bg-muted p-4 rounded-lg">
                <div className="text-2xl font-bold text-text-primary">45</div>
                <div className="text-sm text-text-secondary">Total</div>
              </div>
            </div>

            <div className="flex justify-center space-x-3">
              <Button variant="outline" onClick={handleReset}>
                Upload Another
              </Button>
              <Button 
                onClick={() => window.location?.reload()}
                iconName="RefreshCw"
                iconPosition="left"
              >
                Refresh Inventory
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkUploadPanel;